import { Box } from '@mui/material';
import {
  PipelineList,
  RevenueByMonthChart,
  StatTile,
  type PipelineStageRow,
  type RevenueByMonthWidget,
} from '../../dashboard-widgets';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { usePermissions } from '../../../contexts/AuthContext';
import { hasPermission, P } from '../../../utils/permissions';
import type { ScopedRevenueSummary } from '../../../services/scopedRevenueSummary';
import type { DashboardConfig } from '../types';

interface FinancialsSectionProps {
  /** The server's figures for this range (`scope=dashboard`); `null` until they land. */
  summary: ScopedRevenueSummary | null;
  /** Which of a project's dates the Revenue tile tests against the range. */
  revenueCalculationMethod: DashboardConfig['revenueCalculationMethod'];
  dateRangeLabel: string;
  revenueByMonth: RevenueByMonthWidget | undefined;
  loading: boolean;
  onConfigureActiveProjects: () => void;
  onConfigureRevenue: () => void;
  onConfigurePipeline: () => void;
  onOpenReports?: () => void;
  onViewAllProjects?: () => void;
}

/** What the Revenue tile's date method selects, in the words its sub-line uses. */
const REVENUE_SELECTION: Record<DashboardConfig['revenueCalculationMethod'], string> = {
  START_DATE: 'Completed projects starting in range',
  END_DATE: 'Completed projects ending in range',
  EITHER_DATE: 'Completed projects starting or ending in range',
};

/**
 * `Financials` section — 4 KPI tiles (Active Projects · In-flight value
 * · Delivered revenue · Pipeline Value) atop a 2-panel chart row (Revenue by month
 * + Project pipeline).
 *
 * The chip toggle finally works: this entire block is gated by
 * `isVisible('financials')` in `Dashboard.tsx`, so toggling Financials
 * off removes both the tiles AND the charts. The KpiStrip top-bar is
 * gone; financial KPIs live exclusively here.
 *
 * Permission gating:
 * - Section gate (`VIEW_PROJECT_VALUE`) lives in `sections.tsx`.
 * - Active Projects + In-flight value tiles are non-financial counts of
 *   active projects — they're gated above by `VIEW_PROJECT_VALUE` since
 *   "In-flight value" carries a £ figure.
 * - Revenue + Pipeline Value tiles + the £-bearing PipelineList values
 *   gate again on `VIEW_PROJECT_VALUE` here (defence in depth).
 */
export function FinancialsSection({
  summary,
  revenueCalculationMethod,
  dateRangeLabel,
  revenueByMonth,
  loading,
  onConfigureActiveProjects,
  onConfigureRevenue,
  onConfigurePipeline,
  onOpenReports,
  onViewAllProjects,
}: FinancialsSectionProps) {
  const { formatCurrency } = useCurrency();
  const { permissions } = usePermissions();
  const showValues = hasPermission(permissions, P.VIEW_PROJECT_VALUE);
  const money = (value: number | null | undefined) =>
    value == null ? '—' : formatCurrency(value);

  // With no summary (a failed read) nothing is known, so no count reads as zero.
  const activeCount = summary?.inFlight.count;
  const growthPct = summary?.delivered.growthPct;
  const projects = (n: number) => `${n} project${n === 1 ? '' : 's'}`;

  const pipelineHint = (() => {
    if (!summary) return '—';
    const { count, value } = summary.pipeline;
    if (count === 0) return 'No projects starting this period';
    // An absent value is one this reader may not see, not one nobody set.
    return value === 0
      ? `${projects(count)} starting this period (no amounts set)`
      : `${projects(count)} starting this period`;
  })();

  // The six busiest stages, in the server's order (most projects first), with a
  // stable bar tint per position.
  const stageRows: PipelineStageRow[] = (summary?.stages ?? []).slice(0, 6).map((stage, i) => ({
    name: stage.name,
    count: stage.count,
    value: stage.value,
    sharePct: stage.sharePct,
    barIndex: ((i % 8) + 1) as PipelineStageRow['barIndex'],
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
        }}
      >
        <StatTile
          label="Active projects"
          labelTooltip="Projects where the current date falls within the project's start and end dates"
          value={activeCount ?? '—'}
          sub={activeCount == null ? '—' : `${projects(activeCount)} in ${dateRangeLabel}`}
          onConfigure={onConfigureActiveProjects}
          configureAriaLabel="Configure active projects scope"
          loading={loading}
          data-testid="stat-active-projects"
        />
        <StatTile
          label="In-flight value"
          labelTooltip="Sum of project revenue for all active projects in the selected scope, each at its whole value (not pro-rated to the range), resolved by each project's pricing model — Fixed takes the agreed contract value, Variable is calculated from allocations, Capped is the lesser of the two."
          value={money(summary?.inFlight.value)}
          sub="Projects under way now, each at its whole value"
          valueColor="success.main"
          onConfigure={onConfigureActiveProjects}
          configureAriaLabel="Configure active projects scope"
          loading={loading}
          data-testid="stat-value-in-progress"
        />
        <StatTile
          label={`Delivered revenue (${dateRangeLabel})`}
          labelTooltip="Chargeable revenue of completed, non-cancelled projects whose start date, end date, or either (the revenue date method set in this tile's configuration) falls in the selected period, each at its whole value. Work still under way is in In-flight value, not here."
          value={money(summary?.delivered.value)}
          sub={REVENUE_SELECTION[revenueCalculationMethod]}
          valueColor="success.main"
          trend={growthPct == null ? undefined : { value: growthPct }}
          onConfigure={onConfigureRevenue}
          configureAriaLabel="Configure revenue tile"
          loading={loading}
          data-testid="stat-revenue"
        />
        <StatTile
          label="Pipeline value"
          labelTooltip="Total value of projects with start dates in the selected period that have not yet begun"
          value={money(summary?.pipeline.value)}
          sub={pipelineHint}
          valueColor="primary.main"
          onConfigure={onConfigurePipeline}
          configureAriaLabel="Configure pipeline value tile"
          loading={loading}
          data-testid="stat-pipeline-value"
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(0, 1fr)' },
          gap: 2,
        }}
      >
        <RevenueByMonthChart
          data={revenueByMonth}
          loading={loading}
          onOpenReports={onOpenReports}
        />
        <PipelineList
          rows={stageRows}
          showValues={showValues}
          totalCount={summary?.book.projects}
          onViewAll={onViewAllProjects}
          loading={loading}
        />
      </Box>
    </Box>
  );
}
